/**
 * Phase 4b — Ads API helpers.
 */
import { api } from "@/lib/api";

// ---------- Analytics ----------
export const adsAnalytics = (days = 30) =>
    api.get(`/admin/ads/analytics?days=${days}`).then((r) => r.data);

// ---------- Banners ----------
export const listBanners = (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") q.set(k, v);
    });
    return api.get(`/admin/banner-ads?${q}`).then((r) => r.data);
};
export const createBanner = (body) =>
    api.post("/admin/banner-ads", body).then((r) => r.data);
export const updateBanner = (id, body) =>
    api.patch(`/admin/banner-ads/${id}`, body).then((r) => r.data);
export const pauseBanner = (id) =>
    api.post(`/admin/banner-ads/${id}/pause`).then((r) => r.data);
export const resumeBanner = (id) =>
    api.post(`/admin/banner-ads/${id}/resume`).then((r) => r.data);
export const deleteBanner = (id, force = false) =>
    api.delete(`/admin/banner-ads/${id}${force ? "?force=true" : ""}`).then((r) => r.data);

// ---------- Public banners ----------
export const fetchPublicBanners = ({ district_slug, placement, limit = 10 } = {}) => {
    const q = new URLSearchParams();
    if (district_slug) q.set("district_slug", district_slug);
    if (placement) q.set("placement", placement);
    q.set("limit", String(limit));
    return api.get(`/ads/banners?${q}`).then((r) => r.data);
};
export const trackBannerClick = (id) =>
    api.post(`/ads/banners/${id}/track-click`).then((r) => r.data).catch(() => null);

// ---------- Videos ----------
export const listAdminVideos = (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") q.set(k, v);
    });
    return api.get(`/admin/video-ads?${q}`).then((r) => r.data);
};
export const approveVideo = (id) =>
    api.post(`/admin/video-ads/${id}/approve`).then((r) => r.data);
export const rejectVideo = (id, reason) =>
    api.post(`/admin/video-ads/${id}/reject`, { reason }).then((r) => r.data);
export const pauseVideo = (id) =>
    api.post(`/admin/video-ads/${id}/pause`).then((r) => r.data);
export const resumeVideo = (id) =>
    api.post(`/admin/video-ads/${id}/resume`).then((r) => r.data);
export const deleteAdminVideo = (id) =>
    api.delete(`/admin/video-ads/${id}`).then((r) => r.data);

// ---------- Merchant videos ----------
export const listMyVideos = () => api.get("/me/video-ads").then((r) => r.data);
export const createMyVideo = (body) => api.post("/me/video-ads", body).then((r) => r.data);
export const deleteMyVideo = (id) => api.delete(`/me/video-ads/${id}`).then((r) => r.data);

export const uploadVideo = (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("purpose", "video_ad");
    return api
        .post("/uploads/video", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
                if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
            },
        })
        .then((r) => r.data);
};

export const uploadImage = (file, purpose = "banner") => {
    const fd = new FormData();
    fd.append("file", file);
    // Server enum currently accepts poster/verification_doc/misc/video_ad/banner
    fd.append("purpose", purpose);
    return api
        .post("/uploads", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
};

// ---------- Sponsored ----------
export const listSponsored = (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") q.set(k, v);
    });
    return api.get(`/admin/sponsored-listings?${q}`).then((r) => r.data);
};
export const createSponsored = (body, force = false) =>
    api.post(`/admin/sponsored-listings${force ? "?force=true" : ""}`, body).then((r) => r.data);
export const updateSponsored = (id, body) =>
    api.patch(`/admin/sponsored-listings/${id}`, body).then((r) => r.data);
export const pauseSponsored = (id) =>
    api.post(`/admin/sponsored-listings/${id}/pause`).then((r) => r.data);
export const resumeSponsored = (id) =>
    api.post(`/admin/sponsored-listings/${id}/resume`).then((r) => r.data);
export const deleteSponsored = (id) =>
    api.delete(`/admin/sponsored-listings/${id}`).then((r) => r.data);

// ---------- Constants ----------
export const BANNER_PLACEMENTS = [
    { value: "HOME_TOP", label: "Home top (hero)", hint: "Full-width banner at the top of the customer feed" },
    { value: "HOME_INLINE", label: "Home inline", hint: "Appears after the first 6 cards in the grid" },
    { value: "SHELF_BOTTOM", label: "Shelf bottom", hint: "Appears at the bottom of each shelf" },
];

export const BANNER_STATUS_TONE = {
    DRAFT: "border-neutral-300 bg-neutral-100 text-neutral-700",
    SCHEDULED: "border-blue-300 bg-blue-50 text-blue-700",
    LIVE: "border-emerald-300 bg-emerald-50 text-emerald-700",
    PAUSED: "border-amber-300 bg-amber-50 text-amber-700",
    EXPIRED: "border-neutral-300 bg-neutral-100 text-neutral-500",
};

export const VIDEO_STATUS_TONE = {
    PENDING_REVIEW: "border-amber-300 bg-amber-50 text-amber-800",
    APPROVED: "border-blue-300 bg-blue-50 text-blue-700",
    LIVE: "border-emerald-300 bg-emerald-50 text-emerald-700",
    PAUSED: "border-amber-300 bg-amber-50 text-amber-700",
    REJECTED: "border-rose-300 bg-rose-50 text-rose-700",
    EXPIRED: "border-neutral-300 bg-neutral-100 text-neutral-500",
};

export function humanBytes(n) {
    if (!n && n !== 0) return "—";
    const kb = n / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
}

export function formatDur(s) {
    if (!s && s !== 0) return "—";
    return `${s}s`;
}
